import { useEffect, useState } from "react";
import { CreditCard, Crown, Gift, LoaderCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TX_STATUS = {
  paid: { label: "Payé", cls: "bg-emerald-100 text-emerald-700" },
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
  failed: { label: "Échoué", cls: "bg-rose-100 text-rose-600" },
  expired: { label: "Expiré", cls: "bg-slate-100 text-slate-500" },
};

export default function AdminPremium() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/premium").then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger les données Premium."));
  }, []);

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="page-admin-premium">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Abonnements Premium</h1>
        <p className="mt-1 text-sm text-slate-500">Abonnés, paiements Stripe et revenus de l'offre Premium (7,99 €/mois).</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="card-integra p-5" data-testid="admin-premium-active">
          <Crown className="h-4 w-4 text-amber-500" />
          <p className="mt-2 font-display text-2xl font-extrabold text-slate-900">{data.counts.stripe_active}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Abonnés actifs</p>
        </div>
        <div className="card-integra p-5" data-testid="admin-premium-bonus">
          <Gift className="h-4 w-4 text-emeraude" />
          <p className="mt-2 font-display text-2xl font-extrabold text-slate-900">{data.counts.bonus_only}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Premium via parrainage</p>
        </div>
        <div className="card-integra p-5" data-testid="admin-premium-paid-total">
          <CreditCard className="h-4 w-4 text-bleu" />
          <p className="mt-2 font-display text-2xl font-extrabold text-slate-900">
            {data.totals.paid_amount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Encaissé ({data.totals.paid_count} paiements)</p>
        </div>
        <div className="card-integra p-5" data-testid="admin-premium-failed">
          <XCircle className="h-4 w-4 text-rouge" />
          <p className="mt-2 font-display text-2xl font-extrabold text-slate-900">{data.counts.failed_expired}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Échoués / expirés</p>
        </div>
      </div>

      <section className="card-integra p-6" data-testid="admin-premium-subscribers">
        <h2 className="text-sm font-bold text-slate-900">Abonnés Premium ({data.subscribers.length})</h2>
        {data.subscribers.length === 0 ? (
          <p className="mt-4 text-center text-xs text-slate-400">Aucun abonné Premium pour le moment.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {data.subscribers.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 px-4 py-3" data-testid="admin-premium-subscriber-row">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-slate-900">{s.name || "—"}</p>
                  <p className="text-[11px] text-slate-500">{s.email}</p>
                </div>
                {s.plan === "premium" && s.stripe && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold text-emerald-700">Abonnement Stripe</span>}
                {s.plan !== "premium" && <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-700">Bonus parrainage</span>}
                {s.premium_until && <span className="text-[10px] text-slate-400">jusqu'au {new Date(s.premium_until).toLocaleDateString("fr-FR")}</span>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card-integra p-6" data-testid="admin-premium-transactions">
        <h2 className="text-sm font-bold text-slate-900">Dernières transactions Stripe ({data.transactions.length})</h2>
        {data.transactions.length === 0 ? (
          <p className="mt-4 text-center text-xs text-slate-400">Aucune transaction enregistrée.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {data.transactions.map((t) => {
              const st = TX_STATUS[t.payment_status] || TX_STATUS.pending;
              return (
                <div key={t.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 px-4 py-3" data-testid="admin-premium-tx-row">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-900">
                      {t.purpose === "partner" ? "Abonnement partenaire" : "Abonnement Premium"}
                      {t.lookup_key && <span className="ml-1 font-normal text-slate-400">({t.lookup_key})</span>}
                    </p>
                    <p className="text-[11px] text-slate-500">{t.created_at && new Date(t.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  <span className="text-xs font-bold text-slate-700">{t.amount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}/mois</span>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${st.cls}`}>{st.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
